import { useEffect, useState, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import AppointmentCalendar from '@/components/appointment-calendar';
import AppointmentList from '@/components/appointment-list';
import '@/assets/styles/views/coach/appointments.scss';
import { CalendarDay } from '@/components/appointment-calendar/appointment-calendar.types';
import { AppointmentData } from '@/components/appointment-list/appointment-list.types';
import { useApi } from '@/api/useApi';
import { useQueryProvider } from '@/hooks';
import { CalendarResponse, CalendarSlot } from '@/screens/coach/appointments/appointments.types.ts';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { AppointmentCreateRequestLiveServiceTypeEnum } from '@dopingtech/live_expert_bff_api_ts';
import { ManualCalendarSlotUpdateRequestLiveServiceTypeEnum } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import { DialogModal } from '@/components';

const TIMEZONE = 'Europe/Istanbul';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault(TIMEZONE);

const PlannedAppointmentsPage = () => {
	const { CalendarControllerApi, AppointmentControllerApi } = useApi();

	const [selectedDate, setSelectedDate] = useState(dayjs().tz(TIMEZONE));
	const [allAppointments, setAllAppointments] = useState<{
		active: AppointmentData[];
		cancelled: AppointmentData[];
	}>({ active: [], cancelled: [] });
	const [calendarData, setCalendarData] = useState<CalendarSlot[]>([]);
	const [activeTab, setActiveTab] = useState<'active' | 'cancelled'>('active');
	const [counts, setCounts] = useState({ active: 0, cancelled: 0 });
	const [cancellationCount, setCancellationCount] = useState(0);
	const [hasUnsavedSlotChanges, setHasUnsavedSlotChanges] = useState(false);
	const [pendingDate, setPendingDate] = useState<Date | null>(null);
	const [showUnsavedWarningModal, setShowUnsavedWarningModal] = useState(false);
	const [selectedDay, setSelectedDay] = useState<CalendarDay>({
		date: selectedDate.toDate(),
		isCurrentMonth: true,
		dayName: selectedDate.format('ddd'),
		stats: {
			value: '0',
			isZero: true
		},
		hasAppointment: false,
		hasSlot: true
	});

	useQueryProvider(
		[QUERY_KEYS.countExpertCalendarSlotAppointmentBetweenDates, selectedDate.format('YYYY-MM'), AppointmentCreateRequestLiveServiceTypeEnum.PlannedLecture],
		() => CalendarControllerApi.countExpertCalendarSlotAppointmentBetweenDates(selectedDate.year(), selectedDate.month() + 1, AppointmentCreateRequestLiveServiceTypeEnum.PlannedLecture),
		{
			onSuccess: (response: CalendarResponse) => {
				if (response?.data) {
					setCalendarData(response?.data?.data);
				} else {
					setCalendarData([]);
				}
			}
		}
	);

	useQueryProvider(
		[QUERY_KEYS.getAppointmentCancellationCount, selectedDate.format('YYYY-MM'), AppointmentCreateRequestLiveServiceTypeEnum.PlannedLecture],
		() => AppointmentControllerApi.getAppointmentCancellationCount(selectedDate.year(), selectedDate.month() + 1, AppointmentCreateRequestLiveServiceTypeEnum.PlannedLecture),
		{
			onSuccess: response => {
				if (response?.data?.data !== undefined) {
					setCancellationCount(response.data.data);
				}
			}
		}
	);

	const { isLoading: isLoadingActive } = useQueryProvider(
		[QUERY_KEYS.findAppointmentsOfTheDay, 'planned', selectedDate.tz(TIMEZONE).format('YYYY-MM-DD'), 'CREATED', AppointmentCreateRequestLiveServiceTypeEnum.PlannedLecture],
		() =>
			AppointmentControllerApi.findAppointmentsOfTheDay(selectedDate.tz(TIMEZONE).format('YYYY-MM-DD'), 'CREATED', AppointmentCreateRequestLiveServiceTypeEnum.PlannedLecture, undefined, 200, ['appointmentStartDate,ASC']),
		{
			enabled: selectedDay?.stats?.isZero === false,
			refetchOnWindowFocus: true,
			refetchOnMount: 'always',
			onSuccess: response => {
				if (response) {
					setAllAppointments(prev => ({
						...prev,
						active: response?.data?.data || []
					}));
					setCounts(prev => ({
						...prev,
						active: response.data.data.totalElements || 0
					}));
				}
			}
		}
	);

	const { isLoading: isLoadingCancelled } = useQueryProvider(
		[QUERY_KEYS.findAppointmentsOfTheDay, 'planned', selectedDate.tz(TIMEZONE).format('YYYY-MM-DD'), 'CANCELLED', AppointmentCreateRequestLiveServiceTypeEnum.PlannedLecture],
		() =>
			AppointmentControllerApi.findAppointmentsOfTheDay(
				selectedDate.tz(TIMEZONE).format('YYYY-MM-DD'),
				'CANCELLED',
				AppointmentCreateRequestLiveServiceTypeEnum.PlannedLecture,
				undefined,
				200,
				['appointmentStartDate,ASC']
			),
		{
			refetchOnWindowFocus: true,
			refetchOnMount: 'always',
			onSuccess: response => {
				if (response) {
					setAllAppointments(prev => ({
						...prev,
						cancelled: response?.data?.data || []
					}));
					setCounts(prev => ({
						...prev,
						cancelled: response.data.data.totalElements || 0
					}));
				}
			}
		}
	);

	const { currentAppointments } = useMemo(() => {
		return {
			currentAppointments: allAppointments[activeTab]
		};
	}, [allAppointments, activeTab]);

	useEffect(() => {
		const safeCalendarData = Array.isArray(calendarData) ? calendarData : [];
		const formattedDateStr = selectedDate.tz(TIMEZONE).format('YYYY-MM-DD');

		const selectedDayData = safeCalendarData.find(slot => slot.slotDate === formattedDateStr);

		if (selectedDayData) {
			setSelectedDay(prev => ({
				...prev,
				date: selectedDate.toDate(),
				dayName: selectedDate.format('ddd'),
				isCurrentMonth: selectedDate.isSame(dayjs().tz(TIMEZONE), 'month'),
				stats: {
					value: `${selectedDayData.appointmentCount}/${selectedDayData.slotCount}`,
					isZero: selectedDayData.appointmentCount === 0
				},
				hasSlot: selectedDayData.slotCount > 0,
				hasAppointment: selectedDayData.appointmentCount > 0
			}));
		}
	}, [selectedDate, calendarData]);

	const handleMonthChange = useCallback((date: Date) => {
		const newDate = dayjs(date).tz(TIMEZONE);
		setSelectedDate(prev => prev.month(newDate.month()).year(newDate.year()));
	}, []);

	const handleTabChange = useCallback((tab: 'active' | 'cancelled') => {
		setActiveTab(tab);
	}, []);

	const handleDateSelect = useCallback(
		(date: Date) => {
			if (hasUnsavedSlotChanges) {
				setPendingDate(date);
				setShowUnsavedWarningModal(true);
				return;
			}
			setSelectedDate(dayjs(date).tz(TIMEZONE));
			setActiveTab('active');
		},
		[hasUnsavedSlotChanges]
	);

	const handleUnsavedWarningConfirm = useCallback(() => {
		if (pendingDate) {
			setSelectedDate(dayjs(pendingDate).tz(TIMEZONE));
			setActiveTab('active');
		}
		setPendingDate(null);
		setShowUnsavedWarningModal(false);
	}, [pendingDate]);

	const handleUnsavedWarningCancel = useCallback(() => {
		setPendingDate(null);
		setShowUnsavedWarningModal(false);
	}, []);

	return (
		<div className="appointments-page">
			<AppointmentCalendar
				onDayClick={handleDateSelect}
				selectedDate={selectedDate.toDate()}
				calendarData={calendarData}
				onMonthChange={handleMonthChange}
				cancellationCount={cancellationCount}
				liveServiceType={ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.PlannedLecture}
			/>
			<AppointmentList
				appointments={currentAppointments}
				selectedDay={selectedDay}
				isLoading={isLoadingActive || isLoadingCancelled}
				activeTab={activeTab}
				onTabChange={handleTabChange}
				counts={counts}
				cancellationCount={cancellationCount}
				liveServiceType={ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.PlannedLecture}
				onUnsavedChangesChange={setHasUnsavedSlotChanges}
			/>

			<DialogModal
				isOpen={showUnsavedWarningModal}
				onClose={handleUnsavedWarningCancel}
				variant="warning"
				title="Kaydedilmemiş Değişiklikler"
				primaryButtonText="Devam Et"
				secondaryButtonText="Vazgeç"
				onPrimaryClick={handleUnsavedWarningConfirm}
				onSecondaryClick={handleUnsavedWarningCancel}>
				<p>Slot değişiklikleriniz kaydedilmedi. Başka bir güne geçerseniz bu değişiklikler kaybolacak.</p>
			</DialogModal>
		</div>
	);
};

export default PlannedAppointmentsPage;
